import React, { useCallback, useEffect, useState } from "react";
import Styles from "./Login.module.css";
import PersonIcon from "@mui/icons-material/Person";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useDispatch, useSelector } from "react-redux";
import { login } from "../../../Redux/Slices/authSlice";
import { useNavigate } from "react-router-dom";
import VerifiedPopup from "../Modals/Verified Popup/VerifiedPopup";
import handleNav from "../../../helpers/handleActiveRoleNav";
import { loginService } from "../../../Services/ApiCalling/Auth";
import { ErrorMessage } from "../../../commons/Toast/ErrorMessage";
import { activeLoading } from "../../../Redux/Slices/notificationSlice";
import ForgetPassword from "../Forget/ForgetPassword";
import { setActiveRegion } from "../../../Redux/Slices/activeRegionSlice";

// Note: The 'ForgotPopup' import was unused, so I removed it.
// We are using 'ForgetPassword' directly.

const LoginContainer = () => {
  // --- Hooks ---
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // --- Local State ---
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showVerify, setShowVerified] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // --- Redux State ---
  const token = useSelector((state) => state?.persistedReducer?.token);
  const activeRole = useSelector(
    (state) => state?.persistedReducer?.user?.activeRole,
  );

  // --- Effects ---

  /**
   * 💡 FIX: This effect is re-enabled.
   * It checks if a user is already logged in (token exists) and
   * redirects them to their correct dashboard immediately.
   * This prevents a logged-in user from seeing the login page.
   */
  useEffect(() => {
    if (token && activeRole) {
      handleNav(activeRole);
    }
  }, [token, activeRole]);

  // --- Handlers ---

  /**
   * 🚀 RESTRUCTURED: handleLogin
   * - Uses async/await with try...catch...finally for cleaner asynchronous logic.
   * - Centralizes loading state management (local and global) in the `try` and `finally` blocks.
   * - Uses the imported `handleNav` helper for navigation to keep logic DRY (Don't Repeat Yourself).
   * - Removed the 1-second `setTimeout` for a faster, better user experience.
   * - Enabled the `ErrorMessage` on login failure.
   */
  const handleLogin = useCallback(async () => {
    // 1. Set loading states
    setLoading(true);
    dispatch(activeLoading(true));

    const payload = {
      userName: username,
      password: password,
    };

    try {
      // 2. Call the API
      const res = await loginService(payload);
      if (res?.data?.data?.activeRole === "MEMBER") {
        return ErrorMessage("Invalid Login role");
      }

      // 3. Handle success
      if (res?.data?.code === 200 && res?.data?.data) {
        const responseData = res.data.data;

        // Dispatch to Redux state
        dispatch(login(responseData));
        dispatch(setActiveRegion(responseData.activeUserRegionName || ""));
        localStorage.setItem(
          "OTC_V2_ACTIVE_REGION",
          responseData.activeUserRegionName || "",
        );

        // Set token in local storage
        localStorage.setItem("OTC_V2_TOKEN", responseData.token);

        // Navigate user to the correct dashboard
        // setTimeout(() => {
        //   handleNav(responseData.activeRole);
        // }, 2000);
      } else {
        // 4. Handle API error (e.g., wrong password)
        // 💡 FIX: Enabled the error message
        ErrorMessage(res?.data?.error);
      }
    } catch (error) {
      // 5. Handle network/unexpected errors
      console.error("Login failed:", error);
      ErrorMessage("An unexpected error occurred. Please try again.");
    } finally {
      // 6. Stop loading states (this runs on success or failure)
      setLoading(false);
      dispatch(activeLoading(false));
    }
  }, [username, password, dispatch]); // Dependencies for useCallback

  useEffect(() => {
    const handleEnterKey = (e) => {
      if (
        e.key === "Enter" &&
        !loading &&
        !forgotOpen &&
        !showVerify &&
        username.trim() &&
        password.trim()
      ) {
        handleLogin();
      }
    };

    window.addEventListener("keydown", handleEnterKey);

    return () => {
      window.removeEventListener("keydown", handleEnterKey);
    };
  }, [loading, username, password, forgotOpen, showVerify, handleLogin]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !loading) {
      handleLogin();
    }
  };

  const handleReg = useCallback(() => {
    navigate("/register");
  }, [navigate]);

  // Modal close handlers wrapped in useCallback
  const handleVerifyClose = useCallback(() => {
    setShowVerified(false);
  }, []);

  const handleForgotClose = useCallback(() => {
    setForgotOpen(false);
  }, []);

  // --- Render ---

  // If user is already logged in, render nothing while useEffect redirects
  if (token) {
    return null; // Or a loading spinner
  }

  return (
    <>
      {/* --- Modals --- */}
      {showVerify && (
        <VerifiedPopup open={showVerify} handleClose={handleVerifyClose} />
      )}
      {forgotOpen && (
        /**
         * 💡 FIX: Corrected the handleClose prop.
         * It was passing `setForgotOpen` instead of a function that *calls* it.
         */
        <ForgetPassword open={forgotOpen} handleClose={handleForgotClose} />
      )}

      {/* --- Page Content --- */}
      <div className={Styles.loginContainer}>
        <div className={Styles.loginDisplayContainer}>
          <div className={Styles.loginMainCont}>
            {/* Header */}
            <div className={Styles.loginCompDisplayNameCont}>
              <div className={Styles.compNameText}>GNA Energy</div>
              <div className={Styles.platformText}>OTC Platform</div>
            </div>
            <div className={Styles.loginHeadingCont}>
              <div className={Styles.loginHeadingText}>Login</div>
            </div>

            {/* Form Inputs */}
            <div className={Styles.inputMainCOnt}>
              <div className={Styles.loginInputCont}>
                <div className={Styles.loginInputElemCOnt}>Username</div>
                <div className={Styles.inputFieldCont}>
                  <PersonIcon style={{ fontSize: "16px" }} />
                  <div className={Styles.inputField}>
                    {/* <input
                      type="text"
                      name="username"
                      id="username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                    /> */}
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>
              </div>
              <div className={Styles.loginInputCont}>
                <div className={Styles.loginInputElemCOnt}>Password</div>
                <div className={Styles.inputFieldCont}>
                  <LockIcon style={{ fontSize: "16px" }} />
                  <div className={Styles.inputField}>
                    {/* <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    /> */}
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                  <span
                    onClick={() => setShowPassword((prev) => !prev)}
                    style={{ cursor: "pointer" }} // Added for better UX
                  >
                    {showPassword ? (
                      <VisibilityIcon style={{ fontSize: "16px" }} />
                    ) : (
                      <VisibilityOffIcon style={{ fontSize: "16px" }} />
                    )}
                  </span>
                </div>
                <div
                  className={Styles.forgotPasswordCont}
                  onClick={() => setForgotOpen(true)}
                >
                  Forgot Password?
                </div>
              </div>
            </div>

            {/* Actions */}
            <button
              type="button"
              disabled={loading}
              className={Styles.signinButton}
              onClick={handleLogin}
            >
              {loading ? "Please wait..." : "Submit"}
            </button>

            <div className={Styles.regReqContainer}>
              <span>Don’t Have An Account?</span>
              <span className={Styles.regButton} onClick={handleReg}>
                Register
              </span>
            </div>
          </div>

          {/* Footer */}
          <div className={Styles.bottomText}>
            © {new Date().getFullYear()}, <b>GNA Energy</b> Private Limited All
            Rights Reserved
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginContainer;
